import {
  REMINDER_TIMING_ROWS,
  ROLE_COLUMNS,
  type NotificationRole,
  type NotificationSpecifics,
} from '~/helpers/notificationPrefs';
import {
  aggregateSelectState,
  reminderColumnValues,
  reminderSectionValues,
} from '~/helpers/notificationPrefsSelectAll';
import { PrefCheckbox } from './PrefCheckbox';
import { ScrollablePrefsGrid } from './ScrollablePrefsGrid';
import { SectionSelectAll, toCheckboxState } from './SectionSelectAll';

type Props = {
  jobReminders: NotificationSpecifics['jobReminders'];
  disabled?: boolean;
  onToggle: (
    timing: keyof NotificationSpecifics['jobReminders'],
    role: NotificationRole,
    next: boolean
  ) => void;
  onSectionSelectAll: (checked: boolean) => void;
  onColumnSelectAll: (role: NotificationRole, checked: boolean) => void;
};

export const RemindersSpecifics = ({
  jobReminders,
  disabled,
  onToggle,
  onSectionSelectAll,
  onColumnSelectAll,
}: Props) => {
  const sectionState = aggregateSelectState(
    reminderSectionValues(jobReminders)
  );

  return (
    <div className='flex min-w-0 flex-col gap-3'>
      <h3 className='font-semibold text-primary'>Job Reminders</h3>
      <SectionSelectAll
        state={sectionState}
        disabled={disabled}
        aria-label='Job Reminders Select All'
        onChange={onSectionSelectAll}
      />
      <ScrollablePrefsGrid className='grid w-fit grid-cols-[minmax(12rem,auto)_repeat(3,minmax(5.5rem,auto))] items-center gap-x-6 gap-y-3'>
        <div />
        {ROLE_COLUMNS.map((col) => (
          <div
            key={col.key}
            className='max-w-[7rem] text-center text-xs font-semibold leading-snug text-primary'
          >
            {col.label}
          </div>
        ))}

        <div className='text-sm text-primary'>Select All</div>
        {ROLE_COLUMNS.map((col) => {
          const colState = aggregateSelectState(
            reminderColumnValues(jobReminders, col.key)
          );
          return (
            <PrefCheckbox
              key={col.key}
              state={toCheckboxState(colState)}
              aria-label={`Job Reminders Select All — ${col.label}`}
              disabled={disabled}
              onChange={(next) => onColumnSelectAll(col.key, next)}
            />
          );
        })}

        {REMINDER_TIMING_ROWS.map((row) => (
          <div key={row.key} className='contents'>
            <div className='text-sm text-primary'>{row.label}</div>
            {ROLE_COLUMNS.map((col) => (
              <PrefCheckbox
                key={col.key}
                aria-label={`${row.label} — ${col.label}`}
                checked={jobReminders[row.key][col.key]}
                disabled={disabled}
                onChange={(next) => onToggle(row.key, col.key, next)}
              />
            ))}
          </div>
        ))}
      </ScrollablePrefsGrid>
    </div>
  );
};
